import type { Dayjs } from 'dayjs'
import { useQuery } from '@tanstack/react-query'

import type { FieldMask } from '@jotta/grpc-connect-openapi/mask'
import type { PartialMessage } from '@jotta/types/compat'

import type { CollectionRef } from '../../../api/useCollection'

import {
  selectablePhotoFields,
  type TimelinePhoto,
  usePhotoCollectionService,
} from './useTimelineStream'
import { toNanos } from './util'

/** Day selection asks for exactly what a selection needs. */
export const daySelectionMask: PartialMessage<FieldMask> = {
  includes: [
    {
      fieldNumber: 1, // photo
      childMask: { includes: selectablePhotoFields },
    },
  ],
}

/**
 * A photo selected with its day. Taken from {@link TimelinePhoto}, so `actions` and `md5`
 * are required as they are for photos selected while scrolling: a day selection without
 * them would fetch every photo again before the selection bar could show its actions.
 */
export type DaySelectionPhoto = Pick<
  TimelinePhoto,
  | 'id'
  | 'md5'
  | 'ref'
  | 'timestampNanos'
  | 'encodedContentRef'
  | 'videoDurationSeconds'
  | 'isLivePhoto'
  | 'actions'
>

export function usePhotoIdsForDay(
  collectionRef: CollectionRef,
  day: Dayjs,
  {
    enabled,
  }: {
    enabled: boolean
  },
) {
  const service = usePhotoCollectionService()
  return useQuery({
    refetchOnMount: false,
    enabled,
    queryKey: ['photo_ids_for_day', collectionRef, day.toString()],
    queryFn: async (): Promise<DaySelectionPhoto[]> => {
      const startExclusiveTimestampNanos = toNanos(day.startOf('day'))
      const stopExclusiveTimestampNanos = toNanos(day.endOf('day'))

      const restOfDayResponse = service.getCollectionPhotosMasked(
        {
          collectionRef,
          mask: daySelectionMask,
          selection: {
            case: 'range',
            value: {
              startExclusiveTimestampNanos,
              stopExclusiveTimestampNanos,
            },
          },
        },
        {
          timeoutMs: 60_000,
        },
      )

      const photos: DaySelectionPhoto[] = []
      for await (const { photo } of restOfDayResponse) {
        if (!photo) {
          continue
        }
        const {
          idDeprecated,
          md5,
          ref,
          timestampNanos,
          encodedContentRef,
          videoDurationSeconds,
          isLivePhoto,
          actions,
        } = photo
        photos.push({
          id: idDeprecated,
          md5,
          ref,
          timestampNanos,
          encodedContentRef,
          videoDurationSeconds,
          isLivePhoto,
          actions,
        })
      }

      return photos
    },
  })
}
